/**
 * Live preview helpers for the « Préparer » wizard — and ONLY for it.
 *
 * Every persisted budget figure (calculated cap, balance, financing need, per-household share,
 * fund call suggestion) is produced by the API in `budget-math.util.ts` and displayed as-is.
 * The wizard, however, edits lines that are not saved yet: no endpoint can total a draft, so
 * the three rules below mirror the API ones so the manager sees his own edits move.
 *
 * They are deliberately the *same* formulas, kept minimal and in one file, so that a change on
 * the API side has a single place to follow here. The split key between households is never
 * recomputed: its percentages always come from `GET /budget/:year`.
 */

export const roundToCents = (value: number): number => Math.round(value * 100) / 100;

export interface DraftAnnualAmountInput {
  amount: number;
  frequencyPerYear: number;
  perHousehold: boolean;
}

/** Mirror of `annualAmountOf`: amount × frequency × (perHousehold ? households : 1). */
export const draftAnnualAmount = (line: DraftAnnualAmountInput, householdCount: number): number => {
  const householdMultiplier = line.perHousehold ? householdCount : 1;
  return roundToCents(line.amount * line.frequencyPerYear * householdMultiplier);
};

/**
 * Mirror of `computeFundCallSuggestion`: what is left to call once the incomes that are not
 * fund calls have been counted, per household and per occurrence, rounded up to the euro so
 * the suggestion never lands short of balancing the budget.
 */
export const draftFundCallAmount = (
  financingNeed: number,
  householdCount: number,
  frequencyPerYear: number
): number | null => {
  if (householdCount <= 0 || frequencyPerYear <= 0) return null;
  return Math.ceil(financingNeed / householdCount / frequencyPerYear);
};

/**
 * « vs N−1 » percent — derived, never stored. Null when there is no N−1 reference or when it
 * is zero, in which case no percentage is meaningful and the field is disabled.
 */
export const variationPercent = (
  amount: number,
  previousYearAmount: number | null | undefined
): number | null => {
  if (previousYearAmount == null || previousYearAmount === 0) return null;
  return Math.round((amount / previousYearAmount - 1) * 1000) / 10;
};

/** Inverse of `variationPercent`: the amount a given increase over N−1 leads to. */
export const amountFromVariationPercent = (
  previousYearAmount: number | null | undefined,
  percent: number
): number | null => {
  if (previousYearAmount == null || previousYearAmount === 0) return null;
  return Math.round(previousYearAmount * (1 + percent / 100));
};

export const formatEuros = (value: number): string =>
  `${Math.round(value).toLocaleString('fr-FR')} €`;

export const formatPercent = (value: number): string =>
  `${value.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;
